import { Observable, Subject } from 'rxjs'
import {
  onBLECharacteristicValueChange,
  offBLECharacteristicValueChange,
  notifyBLECharacteristicValueChange,
  writeBLECharacteristicValue,
  closeBLEConnection,
  getBLEDeviceRSSI,
} from '@chargespot/spjs'
import { encodePacket, decodePacket } from './blePacketCodec'
import { StandPeripheral } from '@/ble/interfaces/standPeripheral'
import { withBridgeCap } from './bridgeCall'
import { BRIDGE_TIMEOUTS_MS } from './bleConfig'

type HandleValueChange = Parameters<typeof onBLECharacteristicValueChange>[0]

// spjs の型に writeType は無いが、post() が options を verbatim で native へ転送し、
// native は writeType を CBCharacteristicWriteType の enum(int) として解釈する
// （Inforich回答 2026-07-03。0=withResponse / 1=withoutResponse）。
// V14 コマンド特性(fff1)は write-without-response 専用で、iOS既定の応答ありだと
// errCode:3 "Writing is not permitted." になるため 1(withoutResponse) を明示する。
// ※ 文字列 'writeNoResponse' は spjs v0.25.0 以降で int へ内部変換される予定。現行(0.16.0)は int を渡す。
export const WRITE_WITHOUT_RESPONSE = 1 // CBCharacteristicWriteType.withoutResponse
export const WRITE_WITH_RESPONSE = 0 // CBCharacteristicWriteType.withResponse
type WriteOptions = Parameters<typeof writeBLECharacteristicValue>[0] & {
  writeType?: number
}

// RSSI取得＋数値検証を1つの述語にまとめる。数値でない/欠落した RSSI での resolve は
// 「成功したが値がない」未知の状態であり、肯定的証拠として扱ってはならないため reject する。
async function readValidatedRssi(
  deviceId: string,
  label: string
): Promise<number> {
  const res = await withBridgeCap(
    Promise.resolve(getBLEDeviceRSSI({ deviceId })),
    BRIDGE_TIMEOUTS_MS.readRssi,
    label
  )
  if (!Number.isFinite(res?.RSSI) || res.RSSI >= 0) {
    throw new Error(
      `getBLEDeviceRSSI resolved without a valid negative RSSI: ${JSON.stringify(
        res
      )}`
    )
  }
  return res.RSSI
}

export type SpjsStandPeripheralParams = {
  deviceId: string
  advertisedName: string | null
  serviceUuid: string
  writeCharacteristicId: string
  notifyCharacteristicId: string
  // write の応答種別。V14 fff1 は write-without-response 専用。protocol ごとに異なる
  // （旧AES は応答あり）ため、値の決定は呼び出し側（接続層/repository）に委ねて注入する。
  writeType: number
  // 切断の畳みで実施する adapter close。central の直列化キュー（AdapterSerializer）を
  // 通すために注入する（peripheral が直接 closeBluetoothAdapter を呼ぶと、pending の
  // open との交差を防げない）。契約: キューへの予約（発行）は呼び出しと同期的に行い、
  // 渡した after の settle を待ってから実 close する（#7706）。reject しない
  // （swallow+warn は central 側で実施）。
  closeAdapter: (after?: Promise<unknown>) => Promise<void>
}

export class SpjsStandPeripheral implements StandPeripheral {
  readonly deviceId: string
  readonly advertisedName: string | null
  readonly serviceUuid: string
  readonly notify$: Observable<Uint8Array>
  // spjs コールバックが本デバイス宛に発火した回数。decodePacket の成否に依らない。
  // notify$ はデコード成功後にしか emit しないため、フィルタ通過直後・decodePacket の
  // 前でインクリメントする。
  rawNotifyCount = 0

  private readonly writeCharacteristicId: string
  private readonly notifyCharacteristicId: string
  private readonly writeType: number
  private readonly closeAdapter: (after?: Promise<unknown>) => Promise<void>
  private readonly notifySubject = new Subject<Uint8Array>()
  private readonly handleValueChange: HandleValueChange

  constructor(params: SpjsStandPeripheralParams) {
    this.deviceId = params.deviceId
    this.advertisedName = params.advertisedName
    this.serviceUuid = params.serviceUuid
    this.writeCharacteristicId = params.writeCharacteristicId
    this.notifyCharacteristicId = params.notifyCharacteristicId
    this.writeType = params.writeType
    this.closeAdapter = params.closeAdapter
    this.notify$ = this.notifySubject.asObservable()

    // spjs の global callback を deviceId/char でフィルタし notify$ へ流す。
    this.handleValueChange = (res) => {
      if (
        res.deviceId === this.deviceId &&
        res.characteristicId.toLowerCase() ===
          this.notifyCharacteristicId.toLowerCase()
      ) {
        // decodePacket(atob)の前でカウントする。不正base64(throw)でもフィルタ通過=
        // 本デバイス宛の発火自体は起きているため、デコード成否に依らず数える。
        this.rawNotifyCount++
        // try は decode だけを覆う。decode 失敗を spjs のコールバック機構へ漏らさない
        // （以後の notify 配送が壊れると「沈黙」が自前バグの産物になる）のが目的で、
        // 購読者側の例外まで握ると原因が decode 失敗として埋もれる。観測は上のカウントで済む。
        let decoded: Uint8Array
        try {
          decoded = decodePacket(res.value)
        } catch (e) {
          console.warn('[ble] notify decode failed (dropped)', e)
          return
        }
        this.notifySubject.next(decoded)
      }
    }
    onBLECharacteristicValueChange(this.handleValueChange)
  }

  // Central.connect から呼ぶ（notify有効化）。ブリッジ無応答でも有界に reject する（#7706）。
  async startNotifications(): Promise<void> {
    await withBridgeCap(
      notifyBLECharacteristicValueChange({
        deviceId: this.deviceId,
        serviceId: this.serviceUuid,
        characteristicId: this.notifyCharacteristicId,
        state: true,
      }),
      BRIDGE_TIMEOUTS_MS.enableNotify,
      'notifyBLECharacteristicValueChange'
    )
  }

  async write(bytes: Uint8Array): Promise<void> {
    const options: WriteOptions = {
      deviceId: this.deviceId,
      serviceId: this.serviceUuid,
      characteristicId: this.writeCharacteristicId,
      value: encodePacket(bytes),
      writeType: this.writeType,
    }
    // AES(with-response) のブリッジ無応答でも有界に reject する（#7706）。
    // V14 は repository の writeV14 が発行時 resolve + .catch のため、この cap の reject も
    // そこで warn 吸収される（#7705 の意味論は不変）。
    await withBridgeCap(
      writeBLECharacteristicValue(options),
      BRIDGE_TIMEOUTS_MS.write,
      'writeBLECharacteristicValue'
    )
  }

  // リンク生存確認 probe（#7709）。RSSI 値の妥当性検証は readValidatedRssi 側で行う。
  async readRssi(): Promise<number> {
    return readValidatedRssi(this.deviceId, 'getBLEDeviceRSSI')
  }

  async disconnect(): Promise<void> {
    // offBLECharacteristicValueChange は native ブリッジ呼び(d("bluetooth/..."))で resolve しない
    // ことがあり（無応答病理の1例目）、await すると disconnect がここで hang して
    // closeBLEConnection へ到達しない。現行spot(bluetooth.ts:74) も fire-and-forget なので合わせる。
    // fire-and-forget でも .catch+warn は必須（stop discovery と同じ規律・#7706）。
    void Promise.resolve(
      offBLECharacteristicValueChange(this.handleValueChange)
    ).catch((e) => console.warn('[ble] off listener failed (ignored)', e))
    this.notifySubject.complete()
    // 死んだ機器への close はブリッジ返信が来ないことがある（電源断hangの直接原因・#7706）。
    const closeConn = withBridgeCap(
      // 同期 throw も rejection に畳む（base は try/finally が担保していた edge の保存・#7706 W-3）
      Promise.resolve().then(() =>
        closeBLEConnection({ deviceId: this.deviceId })
      ),
      BRIDGE_TIMEOUTS_MS.closeConnection,
      'closeBLEConnection'
    )
    // adapter close をキューへ「同期で予約」してから接続 close を待つ（#7706 レビュー指摘の回収）:
    // 予約が即時に入るため、後続の open（ユーザーの即時再試行）はこの close の後ろに並ぶ
    // （JS 発行順の保証。native 側完了順の限界は central.closeAdapterAfter のコメント参照）。
    // 実行順は従来どおり「接続 close → adapter close」（予約 op が closeConn の settle を待つ）。
    const adapterClosed = Promise.resolve(this.closeAdapter(closeConn)).catch(
      () => undefined
    )
    try {
      await closeConn
    } finally {
      // adapter セッションの寿命は接続の寿命と一致させる（#7681）: adapter close の完了を
      // 待ってから settle する。closeAdapter は cap+swallow で必ず settle する（reject しない契約）。
      await adapterClosed
    }
  }
}
